import React from "react";
import Input from "react-phone-number-input/input";
import API from "../../api";
import {hideAlertAfterDelay, validateEmail} from "../../helper";
import "../../assets/css/input.css";
import "../../assets/css/notification.css";

const api = new API();
const WHOLESALE_NAME_MAX_LENGTH = 50;
const WHOLESALE_FIELDS = ["name", "email", "contact_no", "message"];

const sanitizeWholesaleName = value => String(value || '').replace(/[^a-zA-Z\s]/g, '').slice(0, WHOLESALE_NAME_MAX_LENGTH);
const getPhoneDigits = value => String(value || '').replace(/\D/g, '');

class WholesaleContactForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            data: this.getInitialData(),
            errors: {},
            touched: this.getInitialTouchedState(),
            submitAttempted: false,
            disabled: false,
            alertMessage: null,
            alertType: null
        };
        this.alertBox = React.createRef();
        this.handleChange = this.handleChange.bind(this);
        this.handlePhoneChange = this.handlePhoneChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    getInitialData() {
        return {
            name: "",
            email: "",
            contact_no: "",
            message: "",
            restaurant_inquiry_for: "3"
        };
    }

    getInitialTouchedState() {
        return WHOLESALE_FIELDS.reduce((touched, fieldName) => ({
            ...touched,
            [fieldName]: false
        }), {});
    }

    getAllTouchedState() {
        return WHOLESALE_FIELDS.reduce((touched, fieldName) => ({
            ...touched,
            [fieldName]: true
        }), {});
    }

    getFieldValidationError(name, value) {
        const fieldValue = value === null || value === undefined ? "" : value.toString().trim();

        if (name === "name") {
            return fieldValue ? "" : "Enter full name";
        }

        if (name === "email") {
            return fieldValue && validateEmail(fieldValue) ? "" : "Enter valid email address";
        }

        if (name === "contact_no") {
            const phoneDigits = getPhoneDigits(fieldValue);
            return phoneDigits && phoneDigits.length < 10 ? "Enter valid phone number" : "";
        }

        if (name === "message") {
            return fieldValue ? "" : "Enter message";
        }

        return "";
    }

    getFormValidationErrors(data = this.state.data) {
        // One validation source keeps submit, blur, and change-after-blur behavior aligned.
        return WHOLESALE_FIELDS.reduce((errors, fieldName) => {
            const errorMessage = this.getFieldValidationError(fieldName, data[fieldName]);
            if (errorMessage) {
                errors[fieldName] = errorMessage;
            }
            return errors;
        }, {});
    }

    getUpdatedErrors(name, value, currentErrors = this.state.errors) {
        const errors = {...currentErrors};
        const errorMessage = this.getFieldValidationError(name, value);

        if (errorMessage) {
            errors[name] = errorMessage;
        } else {
            delete errors[name];
        }

        return errors;
    }

    showAlert(message, type = "success") {
        this.setState({alertMessage: message, alertType: type}, () => {
            if (this.alertBox.current) {
                this.alertBox.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }
        });

        if (this.alertTimeout) {
            clearTimeout(this.alertTimeout);
        }

        this.alertTimeout = hideAlertAfterDelay(() => {
            this.setState({alertMessage: null, alertType: null});
        });
    }

    handleChange(event) {
        const {name} = event.target;
        const value = name === "name" ? sanitizeWholesaleName(event.target.value) : event.target.value;
        const shouldValidate = this.state.touched[name] || this.state.submitAttempted;

        this.setState(prevState => ({
            data: {
                ...prevState.data,
                [name]: value
            },
            errors: shouldValidate ? this.getUpdatedErrors(name, value, prevState.errors) : prevState.errors
        }));
    }

    handlePhoneChange(value) {
        const phoneValue = value || "";
        const shouldValidate = this.state.touched.contact_no || this.state.submitAttempted;

        this.setState(prevState => ({
            data: {
                ...prevState.data,
                contact_no: phoneValue
            },
            errors: shouldValidate ? this.getUpdatedErrors("contact_no", phoneValue, prevState.errors) : prevState.errors
        }));
    }

    handleBlur(event) {
        const {name} = event.target;

        this.setState(prevState => ({
            touched: {
                ...prevState.touched,
                [name]: true
            },
            errors: this.getUpdatedErrors(name, prevState.data[name], prevState.errors)
        }));
    }

    handleSubmit(event) {
        event.preventDefault();
        const errors = this.getFormValidationErrors();

        this.setState({
            errors,
            touched: this.getAllTouchedState(),
            submitAttempted: true
        });

        if (Object.keys(errors).length) {
            return false;
        }

        this.setState({disabled: true});
        api.contactUs(this.state.data)
            .then(() => {
                this.setState({
                    data: this.getInitialData(),
                    errors: {},
                    touched: this.getInitialTouchedState(),
                    submitAttempted: false,
                    disabled: false
                });
                this.showAlert("Thank you for your message. A Farmer's Fresh Team Member will get back with you shortly!");
            })
            .catch(error => {
                const errorMessage = error?.response?.data?.message || "Unable to send your message. Please try again.";
                this.setState({disabled: false});
                this.showAlert(errorMessage, "error");
            });
    }

    render() {
        const {data, errors, disabled, alertMessage, alertType} = this.state;
        const isErrorAlert = alertType === "error";

        return (
            <form className="col-12 form-group p-0" onSubmit={this.handleSubmit} noValidate>
                {alertMessage ? (
                    <small
                        ref={this.alertBox}
                        role="alert"
                        className={"alert-message fs-12 sc-1wz9i2x-0 mt-1 " + (isErrorAlert ? "fooVeK" : "fooVeKSuccess")}
                    >
                        <small className={"text-message fs-12 " + (isErrorAlert ? "text-danger" : "")}>
                            {alertMessage}
                        </small>
                    </small>
                ) : null}
                <div className="row">
                    <div className="col-md-12 mb-3 fields px-3">
                        <label htmlFor="wholesale-name" className="form-label">
                            Full Name *
                        </label>
                        <input
                            id="wholesale-name"
                            type="text"
                            className={"form-control valid-control " + (errors.name ? "invalid" : "")}
                            name="name"
                            placeholder="Enter full name"
                            value={data.name}
                            maxLength={WHOLESALE_NAME_MAX_LENGTH}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                        />
                        <span className="error-text mt-1">{errors.name}</span>
                    </div>
                    <div className="col-md-12 mb-3 fields px-3">
                        <label htmlFor="wholesale-email" className="form-label">
                            Email *
                        </label>
                        <input
                            id="wholesale-email"
                            type="text"
                            className={"form-control valid-control " + (errors.email ? "invalid" : "")}
                            placeholder="Email address"
                            name="email"
                            value={data.email}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                        />
                        <span className="error-text mt-1">{errors.email}</span>
                    </div>
                    <div className="col-md-12 mb-3 fields px-3">
                        <label htmlFor="wholesale-phone" className="form-label">
                            Phone
                        </label>
                        <Input
                            id="wholesale-phone"
                            international
                            withCountryCallingCode
                            maxLength="15"
                            country="US"
                            name="contact_no"
                            className={"form-control valid-control " + (errors.contact_no ? "invalid" : "")}
                            placeholder="Enter phone number"
                            value={data.contact_no}
                            onChange={this.handlePhoneChange}
                            onBlur={this.handleBlur}
                        />
                        <span className="error-text mt-1">{errors.contact_no}</span>
                    </div>
                    <div className="col-md-12 mb-3 fields px-3">
                        <label htmlFor="wholesale-message" className="form-label">
                            Message *
                        </label>
                        <textarea
                            id="wholesale-message"
                            className={"form-control valid-control " + (errors.message ? "invalid" : "")}
                            name="message"
                            value={data.message}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                        />
                        <span className="error-text mt-1">{errors.message}</span>
                    </div>
                    <div className="col-md-12 fields px-3">
                        <button type="submit" disabled={disabled} className="btn btn-order py-2 px-4">
                            Send
                        </button>
                    </div>
                </div>
            </form>
        );
    }
}

export default WholesaleContactForm;
